import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { BuildingIcon, HomeIcon, ListIcon, UserIcon, WrenchIcon } from "../ui/icons.js";
import { Wordmark } from "../ui/Wordmark.js";
import { useIsAdmin } from "../auth/useIsAdmin.js";
import { EmailVerificationProvider, useEmailVerification } from "../auth/EmailVerification.js";

const baseNavItems = [
  { to: "/app", label: "Home", end: true, icon: HomeIcon },
  { to: "/app/properties", label: "Properties", end: false, icon: BuildingIcon },
  { to: "/app/rfqs", label: "Requests", end: false, icon: ListIcon },
  { to: "/app/account", label: "Account", end: false, icon: UserIcon },
] as const;

const adminNavItem = {
  to: "/admin",
  label: "Admin",
  end: false,
  icon: WrenchIcon,
} as const;

/**
 * Authenticated app shell. Bottom-nav pattern, mobile-first.
 *
 * Visual notes (design.md §6):
 *   - Active tab: brand-700 icon + label, soft brand pill behind icon.
 *   - Inactive: slate-500.
 *   - Subtle press scale + color transition gives the tap a sense of weight
 *     without leaning on a heavy ripple effect.
 */
export function AppShell() {
  return (
    <EmailVerificationProvider>
      <AppShellInner />
    </EmailVerificationProvider>
  );
}

function AppShellInner() {
  const { loading, isAdmin } = useIsAdmin();
  const navItems = !loading && isAdmin ? [...baseNavItems, adminNavItem] : baseNavItems;
  return (
    <div className="flex min-h-dvh flex-col bg-slate-50">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex h-20 max-w-md items-center px-4">
          <Wordmark className="h-auto w-[240px]" />
        </div>
      </header>
      <VerifyEmailBanner />
      <main className="flex-1 overflow-y-auto pb-24">
        <Outlet />
      </main>
      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-10 border-t border-slate-200 bg-white/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="mx-auto flex max-w-md justify-around">
          {navItems.map(({ to, label, end, icon: Icon }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  [
                    "press flex min-h-[60px] flex-col items-center justify-center gap-1 px-2 py-2 text-xs font-medium",
                    isActive ? "text-brand-700" : "text-slate-500 hover:text-slate-700",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={[
                        "flex h-7 w-12 items-center justify-center rounded-full transition-colors",
                        isActive ? "bg-brand-50" : "bg-transparent",
                      ].join(" ")}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span>{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/**
 * Persistent nudge for users whose email isn't verified yet (i.e. they skipped
 * verification after the auto-confirmed signup). Hidden on the verify screen
 * itself. Keeps the door to password recovery open without blocking the app.
 */
function VerifyEmailBanner() {
  const { loading, verified } = useEmailVerification();
  const location = useLocation();
  if (loading || verified || location.pathname === "/app/verify-email") return null;
  return (
    <div className="border-b border-amber-200 bg-amber-50">
      <div className="mx-auto flex max-w-md items-center justify-between gap-3 px-4 py-2.5">
        <p className="text-sm text-amber-900">Verify your email to secure your account.</p>
        <Link
          to="/app/verify-email"
          className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white active:bg-amber-700"
        >
          Verify
        </Link>
      </div>
    </div>
  );
}
